import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function FloridaToIreland() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navbar scrolled={scrolled} />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/a1f5b0426ec2b089e68a8e7b5526fde2.jpg"
            alt="Florida to Ireland relocation"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60"></div>
        </div>

        <div className="relative max-w-5xl mx-auto text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Moving to Ireland from Florida</h1>
          <p className="text-xl md:text-2xl mb-8 text-white/90">Trading sunshine for green landscapes: Your complete Florida to Ireland relocation guide</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to="/tools/cost-calculator"
              className="px-8 py-4 bg-[#D4AF37] hover:bg-[#C19B2F] text-white rounded-lg font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              Compare Costs
            </Link>
            <Link
              to="/directory"
              className="px-8 py-4 bg-white hover:bg-gray-100 text-[#0A381F] rounded-lg font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              Find Services
            </Link>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="py-12 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center p-6 bg-gradient-to-br from-[#0A381F] to-[#0D4A28] rounded-2xl text-white">
              <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-4">
                <i className="ri-flight-takeoff-line text-3xl"></i>
              </div>
              <div className="text-3xl font-bold mb-2">8-9 hours</div>
              <div className="text-white/80">Direct flight time</div>
            </div>
            <div className="text-center p-6 bg-gradient-to-br from-[#D4AF37] to-[#C19B2F] rounded-2xl text-white">
              <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-4">
                <i className="ri-time-line text-3xl"></i>
              </div>
              <div className="text-3xl font-bold mb-2">+5 hours</div>
              <div className="text-white/80">Time difference</div>
            </div>
            <div className="text-center p-6 bg-gradient-to-br from-purple-600 to-purple-700 rounded-2xl text-white">
              <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-4">
                <i className="ri-temp-cold-line text-3xl"></i>
              </div>
              <div className="text-3xl font-bold mb-2">50-60°F</div>
              <div className="text-white/80">Average temperature</div>
            </div>
            <div className="text-center p-6 bg-gradient-to-br from-green-600 to-green-700 rounded-2xl text-white">
              <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-4">
                <i className="ri-money-dollar-circle-line text-3xl"></i>
              </div>
              <div className="text-3xl font-bold mb-2">No State Tax</div>
              <div className="text-white/80">In both locations!</div>
            </div>
          </div>
        </div>
      </section>

      {/* Biggest Adjustments */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-[#0A381F] mb-4 text-center">Biggest Adjustments for Floridians</h2>
          <p className="text-xl text-gray-600 mb-12 text-center max-w-3xl mx-auto">
            Moving from Florida to Ireland means significant lifestyle changes - here's what to expect
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="w-14 h-14 flex items-center justify-center bg-blue-100 rounded-full mb-6">
                <i className="ri-cloud-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-[#0A381F] mb-4">Weather Shock</h3>
              <p className="text-gray-600 mb-4">
                This is the biggest change. Ireland averages 50-60°F year-round with frequent rain. No more 90°F sunny days or beach weather.
              </p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Miami Average:</span>
                  <span className="font-semibold">77°F</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Dublin Average:</span>
                  <span className="font-semibold">50°F</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Sunny Days/Year:</span>
                  <span className="font-semibold">248 → 60</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="w-14 h-14 flex items-center justify-center bg-orange-100 rounded-full mb-6">
                <i className="ri-car-line text-2xl text-orange-600"></i>
              </div>
              <h3 className="text-xl font-bold text-[#0A381F] mb-4">Car Culture</h3>
              <p className="text-gray-600 mb-4">
                Florida is car-dependent. Ireland has excellent public transit - most people in Dublin don't own cars. Plus, driving on the left side takes adjustment!
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-green-600"></i>
                  <span>Walk/bike everywhere</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-green-600"></i>
                  <span>Excellent bus/train system</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-green-600"></i>
                  <span>Gas costs $6.80/gallon</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="w-14 h-14 flex items-center justify-center bg-green-100 rounded-full mb-6">
                <i className="ri-home-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-bold text-[#0A381F] mb-4">Housing Size</h3>
              <p className="text-gray-600 mb-4">
                Florida homes are spacious. Irish apartments and houses are much smaller - think 800-1,200 sq ft vs Florida's 2,000+ sq ft average.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center gap-2">
                  <i className="ri-information-line text-blue-600"></i>
                  <span>No central AC (rarely needed)</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-information-line text-blue-600"></i>
                  <span>Smaller kitchens &amp; bathrooms</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-information-line text-blue-600"></i>
                  <span>Less storage space</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Cost Comparison */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-[#0A381F] mb-4 text-center">Florida vs Ireland: Cost Comparison</h2>
          <p className="text-xl text-gray-600 mb-12 text-center max-w-3xl mx-auto">
            How your Florida expenses translate to Irish living costs
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            {/* Housing Costs */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <h3 className="text-2xl font-bold text-[#0A381F] mb-6">Housing Costs</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                  <div>
                    <div className="font-semibold text-gray-900">1-Bedroom Apartment</div>
                    <div className="text-sm text-gray-500">Miami/Orlando vs Dublin</div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-gray-900">$1,800</div>
                    <div className="text-sm text-gray-600">vs $1,800</div>
                  </div>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                  <div>
                    <div className="font-semibold text-gray-900">2-Bedroom Apartment</div>
                    <div className="text-sm text-gray-500">City center</div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-gray-900">$2,400</div>
                    <div className="text-sm text-red-600">vs $2,600</div>
                  </div>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                  <div>
                    <div className="font-semibold text-gray-900">3-Bedroom House</div>
                    <div className="text-sm text-gray-500">Suburbs</div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-gray-900">$2,200</div>
                    <div className="text-sm text-red-600">vs $2,400</div>
                  </div>
                </div>
                <div className="bg-blue-50 rounded-lg p-4 mt-4">
                  <div className="flex items-center gap-2 text-blue-700 font-semibold">
                    <i className="ri-information-line"></i>
                    <span>Housing costs similar, but homes are smaller in Ireland</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Other Costs */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <h3 className="text-2xl font-bold text-[#0A381F] mb-6">Daily Expenses</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                  <div>
                    <div className="font-semibold text-gray-900">Groceries (Monthly)</div>
                    <div className="text-sm text-gray-500">Single person</div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-gray-900">$350</div>
                    <div className="text-sm text-red-600">vs $450</div>
                  </div>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                  <div>
                    <div className="font-semibold text-gray-900">Utilities + Internet</div>
                    <div className="text-sm text-gray-500">Monthly average</div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-gray-900">$200</div>
                    <div className="text-sm text-green-600">vs $150</div>
                  </div>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                  <div>
                    <div className="font-semibold text-gray-900">Healthcare (Annual)</div>
                    <div className="text-sm text-gray-500">Insurance + costs</div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-gray-900">$6,000</div>
                    <div className="text-sm text-green-600">vs $1,200</div>
                  </div>
                </div>
                <div className="bg-green-50 rounded-lg p-4 mt-4">
                  <div className="flex items-center gap-2 text-green-700 font-semibold">
                    <i className="ri-arrow-down-line"></i>
                    <span>Save $4,800/year on healthcare alone!</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Detailed Comparison Table */}
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
            <h3 className="text-2xl font-bold text-[#0A381F] mb-6">Complete Cost Breakdown</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-4 px-4 font-semibold text-gray-900">Item</th>
                    <th className="text-right py-4 px-4 font-semibold text-gray-900">Florida</th>
                    <th className="text-right py-4 px-4 font-semibold text-gray-900">Ireland</th>
                    <th className="text-right py-4 px-4 font-semibold text-gray-900">Difference</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <tr>
                    <td className="py-4 px-4">Meal at Restaurant</td>
                    <td className="text-right py-4 px-4">$15</td>
                    <td className="text-right py-4 px-4">$20</td>
                    <td className="text-right py-4 px-4 text-red-600">+33%</td>
                  </tr>
                  <tr>
                    <td className="py-4 px-4">Coffee</td>
                    <td className="text-right py-4 px-4">$4</td>
                    <td className="text-right py-4 px-4">$4.50</td>
                    <td className="text-right py-4 px-4 text-red-600">+13%</td>
                  </tr>
                  <tr>
                    <td className="py-4 px-4">Gas (per gallon)</td>
                    <td className="text-right py-4 px-4">$3.20</td>
                    <td className="text-right py-4 px-4">$6.80</td>
                    <td className="text-right py-4 px-4 text-red-600">+113%</td>
                  </tr>
                  <tr>
                    <td className="py-4 px-4">Gym Membership</td>
                    <td className="text-right py-4 px-4">$40</td>
                    <td className="text-right py-4 px-4">$55</td>
                    <td className="text-right py-4 px-4 text-red-600">+38%</td>
                  </tr>
                  <tr>
                    <td className="py-4 px-4">Movie Ticket</td>
                    <td className="text-right py-4 px-4">$12</td>
                    <td className="text-right py-4 px-4">$12</td>
                    <td className="text-right py-4 px-4 text-gray-500">Same</td>
                  </tr>
                  <tr>
                    <td className="py-4 px-4">Beer at Bar</td>
                    <td className="text-right py-4 px-4">$6</td>
                    <td className="text-right py-4 px-4">$7</td>
                    <td className="text-right py-4 px-4 text-red-600">+17%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Jobs & Career */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-[#0A381F] mb-4 text-center">Jobs for Floridians in Ireland</h2>
          <p className="text-xl text-gray-600 mb-12 text-center max-w-3xl mx-auto">
            Florida's growing tech and tourism sectors translate well to Ireland's economy
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-[#0A381F] to-[#0D4A28] rounded-2xl p-8 text-white">
              <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-full mb-6">
                <i className="ri-building-4-line text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Top Industries</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#D4AF37] text-xl mt-1"></i>
                  <span><strong>Technology:</strong> Google, Meta, Microsoft, Amazon all hiring</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#D4AF37] text-xl mt-1"></i>
                  <span><strong>Pharmaceuticals:</strong> Pfizer, Johnson &amp; Johnson, Merck</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#D4AF37] text-xl mt-1"></i>
                  <span><strong>Tourism &amp; Hospitality:</strong> Growing sector, familiar to Floridians</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#D4AF37] text-xl mt-1"></i>
                  <span><strong>Financial Services:</strong> Dublin's IFSC is Europe's financial hub</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#D4AF37] text-xl mt-1"></i>
                  <span><strong>Aviation:</strong> Shannon and Dublin airports expanding</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-[#D4AF37] to-[#C19B2F] rounded-2xl p-8 text-white">
              <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-full mb-6">
                <i className="ri-briefcase-line text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Salary Expectations</h3>
              <div className="space-y-4">
                <div>
                  <div className="text-sm text-white/80 mb-1">Software Engineer</div>
                  <div className="text-2xl font-bold">$85,000-120,000</div>
                  <div className="text-sm text-white/70">Florida: $75,000-110,000</div>
                </div>
                <div>
                  <div className="text-sm text-white/80 mb-1">Marketing Manager</div>
                  <div className="text-2xl font-bold">$60,000-80,000</div>
                  <div className="text-sm text-white/70">Florida: $55,000-75,000</div>
                </div>
                <div>
                  <div className="text-sm text-white/80 mb-1">Hospitality Manager</div>
                  <div className="text-2xl font-bold">$45,000-60,000</div>
                  <div className="text-sm text-white/70">Florida: $40,000-55,000</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lifestyle Comparison */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-[#0A381F] mb-4 text-center">Lifestyle: Florida vs Ireland</h2>
          <p className="text-xl text-gray-600 mb-12 text-center max-w-3xl mx-auto">
            What Floridians love (and miss) about living in Ireland
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* What You'll Love */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 flex items-center justify-center bg-green-100 rounded-full">
                  <i className="ri-heart-line text-2xl text-green-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-[#0A381F]">What You'll Love</h3>
              </div>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-green-600 text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">No Hurricanes:</strong>
                    <p className="text-gray-600">No more evacuation plans, boarding up windows, or hurricane season stress</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-green-600 text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">European Travel:</strong>
                    <p className="text-gray-600">Weekend trips to Paris, Rome, Barcelona for &lt; $100</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-green-600 text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Healthcare Savings:</strong>
                    <p className="text-gray-600">Save $5,000-10,000/year on health insurance and medical costs</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-green-600 text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Walkable Cities:</strong>
                    <p className="text-gray-600">No more driving everywhere - walk or bike to most places</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-green-600 text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Work-Life Balance:</strong>
                    <p className="text-gray-600">25+ vacation days standard, shorter work weeks</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-green-600 text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Pub Culture:</strong>
                    <p className="text-gray-600">Authentic Irish pubs with live music and community atmosphere</p>
                  </div>
                </li>
              </ul>
            </div>

            {/* What You'll Miss */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 flex items-center justify-center bg-orange-100 rounded-full">
                  <i className="ri-emotion-sad-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-[#0A381F]">What You'll Miss</h3>
              </div>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <i className="ri-close-line text-orange-600 text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Sunshine &amp; Warmth:</strong>
                    <p className="text-gray-600">This is the hardest adjustment - Ireland is cool, cloudy, and rainy</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-line text-orange-600 text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Beaches:</strong>
                    <p className="text-gray-600">Ireland has beautiful coastline, but water is too cold for swimming</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-line text-orange-600 text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Cuban Food:</strong>
                    <p className="text-gray-600">Miami's amazing Cuban cuisine doesn't exist in Ireland</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-line text-orange-600 text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Theme Parks:</strong>
                    <p className="text-gray-600">No Disney World, Universal, or major theme parks</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-line text-orange-600 text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Space &amp; Size:</strong>
                    <p className="text-gray-600">Homes, cars, and portions are all much smaller</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-line text-orange-600 text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Air Conditioning:</strong>
                    <p className="text-gray-600">Most homes don't have AC (rarely needed, but summers can be uncomfortable)</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Practical Tips */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-[#0A381F] mb-12 text-center">Practical Tips for Floridians</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-8 text-white">
              <div className="w-14 h-14 flex items-center justify-center bg-white/20 rounded-full mb-6">
                <i className="ri-flight-takeoff-line text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold mb-4">Direct Flights</h3>
              <p className="text-white/90 mb-4">Aer Lingus flies direct from Orlando and Miami to Dublin (8-9 hours). Book 2-3 months ahead for best prices.</p>
              <p className="text-sm text-white/80">Tip: Overnight flights arrive Dublin morning - helps with jet lag</p>
            </div>

            <div className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-2xl p-8 text-white">
              <div className="w-14 h-14 flex items-center justify-center bg-white/20 rounded-full mb-6">
                <i className="ri-t-shirt-line text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold mb-4">Wardrobe Overhaul</h3>
              <p className="text-white/90 mb-4">Pack layers, waterproof jackets, and warm clothes. Your Florida wardrobe won't work in Ireland!</p>
              <p className="text-sm text-white/80">Tip: Buy winter clothes in Ireland - better selection and quality</p>
            </div>

            <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-2xl p-8 text-white">
              <div className="w-14 h-14 flex items-center justify-center bg-white/20 rounded-full mb-6">
                <i className="ri-home-heart-line text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold mb-4">Downsize</h3>
              <p className="text-white/90 mb-4">Irish homes are 50-60% smaller than Florida homes. Sell or store most furniture before moving.</p>
              <p className="text-sm text-white/80">Tip: Shipping costs more than buying new in Ireland</p>
            </div>
          </div>
        </div>
      </section>

      {/* Relocation Expert CTA */}
      <section className="py-16 px-6 bg-gradient-to-br from-[#0A381F] to-[#0D4A28]">
        <div className="max-w-4xl mx-auto text-center text-white">
          <div className="w-20 h-20 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-6">
            <i className="ri-customer-service-2-line text-4xl"></i>
          </div>
          <h2 className="text-4xl font-bold mb-6">Questions About Moving from Florida?</h2>
          <p className="text-xl text-white/90 mb-8">
            Our Ireland Relocation Expert can answer all your questions about visas, housing, weather adjustment, and life in Ireland
          </p>
          <button
            onClick={() => {
              const widget = document.querySelector('#vapi-widget-floating-button') as HTMLElement;
              if (widget) widget.click();
            }}
            className="px-8 py-4 bg-[#D4AF37] hover:bg-[#C19B2F] text-white rounded-lg font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap inline-flex items-center gap-3"
          >
            <i className="ri-chat-3-line text-xl"></i>
            <span>Talk to Relocation Expert</span>
          </button>
        </div>
      </section>

      {/* Next Steps */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-[#0A381F] mb-6">Ready to Make the Move?</h2>
          <p className="text-xl text-gray-600 mb-12">
            Start your relocation journey with our comprehensive tools and resources
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link
              to="/tools/visa-quiz"
              className="p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all cursor-pointer border border-gray-100"
            >
              <div className="w-14 h-14 flex items-center justify-center bg-[#0A381F] rounded-full mx-auto mb-4">
                <i className="ri-questionnaire-line text-2xl text-white"></i>
              </div>
              <h3 className="text-lg font-bold text-[#0A381F] mb-2">Check Visa Eligibility</h3>
              <p className="text-gray-600 text-sm">Find out which visa you qualify for</p>
            </Link>

            <Link
              to="/tools/cost-calculator"
              className="p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all cursor-pointer border border-gray-100"
            >
              <div className="w-14 h-14 flex items-center justify-center bg-[#D4AF37] rounded-full mx-auto mb-4">
                <i className="ri-calculator-line text-2xl text-white"></i>
              </div>
              <h3 className="text-lg font-bold text-[#0A381F] mb-2">Calculate Your Budget</h3>
              <p className="text-gray-600 text-sm">Compare Florida vs Ireland costs</p>
            </Link>

            <Link
              to="/directory"
              className="p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all cursor-pointer border border-gray-100"
            >
              <div className="w-14 h-14 flex items-center justify-center bg-purple-600 rounded-full mx-auto mb-4">
                <i className="ri-building-line text-2xl text-white"></i>
              </div>
              <h3 className="text-lg font-bold text-[#0A381F] mb-2">Find Services</h3>
              <p className="text-gray-600 text-sm">Immigration lawyers, movers, and more</p>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
